"use client";

import { useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { experiments, type ExperimentFlag } from "@/lib/experiments";
import { useExperimentVariant } from "@/hooks/useExperimentVariant";

type Values = Record<string, string | number | Date>;

// Experiment-aware copy hook. Drop-in alongside next-intl: returns a `tx(key)`
// function that yields the assigned variant's copy when an experiment overrides
// that key, and otherwise falls back to the default (control) translation.
//
//   const tx = useCopy("Hero", "hero-headline");
//   <h1>{tx("titleEm")}</h1>
//
// Default render (server / no consent / flags not yet loaded) always returns the
// control copy, so SEO + first paint stay on the indexed default. Reading the
// flag fires PostHog's `$feature_flag_called` exposure event for consented users.
//
// Variant copy lives under `ab.<flag>.<variant>` in the section's messages and is
// plain text (no ICU placeholders); interpolation always uses the default key.
export function useCopy(namespace: string, flag?: ExperimentFlag) {
  const t = useTranslations(namespace);
  const variant = useExperimentVariant(flag);

  const overrides = useMemo<Record<string, string> | null>(() => {
    if (!flag) return null;
    if (typeof variant !== "string") return null;
    if (variant === experiments[flag].variants[0]) return null; // control
    if (!t.has("ab")) return null;
    const ab = t.raw("ab") as
      | Record<string, Record<string, Record<string, string>>>
      | undefined;
    return ab?.[flag]?.[variant] ?? null;
  }, [t, flag, variant]);

  return useCallback(
    (key: string, values?: Values) => {
      if (overrides && key in overrides && values === undefined) {
        return overrides[key];
      }
      return t(key, values);
    },
    [t, overrides],
  );
}
